import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	OnDestroy,
	ViewEncapsulation,
	ChangeDetectorRef,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { SgaLoginAction, SgaLoginTab } from "@obg/common.login/sdk";
import { CustomerSupportService } from "@obg/core.customer-support";
import { TranslatableAlertService } from "@obg/mat.ui.core";
import { LOGIN_DIALOG_ID } from "@obg/ngx.common";
import {
	AuthTransactionAction,
	AuthTransactionSelector,
	AuthenticationType,
	CORE_ROUTE,
	CoreState,
	VerificationStatus,
} from "@obg/ngx.core";
import { ImageContent, ImageSelector, TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { PlatformService, DeviceConfigService } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { BehaviorSubject, Observable, Subscription, combineLatest, interval, merge } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";

import { SGA_LOGIN_FLOW_BANKID_NOT_OPENED_ALERT_ID, SgaLoginUiFlowService } from "../sga-login-flow.service";
import { SgaLoginError } from "../sga-login.model";

import { LOGIN_BANKID_TRANSLATION_KEYS, ViewOptions, QrComponentViewOptions } from "./login-bankid.model";

@Component({
	selector: "obg-m-login-bankid-container",
	templateUrl: "./login-bankid.container.html",
	styleUrls: ["./login-bankid.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class LoginBankIdContainer implements OnInit, OnDestroy {
	view: ViewOptions = ViewOptions.default;
	qrCodeComponentView: QrComponentViewOptions = QrComponentViewOptions.default;

	genericLoginError: string | undefined;

	isDesktop: boolean | undefined;
	invalidCredentialsError: boolean | undefined;
	initializeVerificationFail: boolean | undefined;

	private errorComingFrom$ = new BehaviorSubject<"qr-code-error" | "bank-id-start-error" | undefined>(undefined);

	get errorComingFromObservable$() {
		return this.errorComingFrom$.asObservable();
	}

	qrCode$: Observable<string> | undefined;
	pollingTimeout$: Observable<boolean> | undefined;

	ssr = false;
	images$: Observable<Record<string, ImageContent | undefined>> | undefined;
	translations$: Observable<Record<string, string | undefined>> | undefined;
	qrCodeComponentTranslations$: Observable<Record<string, string | undefined>> | undefined;

	private openNewBankIdFlowApp$: Observable<void> | undefined;
	private openNewBankIdFlowAppSubscription$$: Subscription | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private sgaLoginUiFlowService: SgaLoginUiFlowService,
		private authTransactionAction: AuthTransactionAction,
		private authTransactionSelector: AuthTransactionSelector,
		private store: Store<CoreState>,
		private alertService: TranslatableAlertService,
		private changeDetector: ChangeDetectorRef,
		private customerSupportService: CustomerSupportService,
		private sgaLoginAction: SgaLoginAction,
		private translationSelector: TranslationSelector,
		private deviceConfigService: DeviceConfigService,
		private imageSelector: ImageSelector,
		private platformService: PlatformService,
		private matDialog: MatDialog
	) {
		this.isDesktop = this.deviceConfigService.isDesktop();
		this.ssr = this.platformService.isServer;
	}

	setBankIdView(view: ViewOptions) {
		this.view = view;
	}

	initializeAuthentication() {
		this.store.dispatch(
			this.authTransactionAction.initializeAuthTransaction({ authenticationType: AuthenticationType.BankId })
		);
	}

	cancelLastVerification() {
		this.store.dispatch(
			this.authTransactionAction.cancelLastVerification({ authenticationType: AuthenticationType.BankId })
		);
	}

	onBankIdOpenEvent() {
		this.resetValues();
		this.errorComingFrom$.next("bank-id-start-error");
		this.setBankIdView(ViewOptions.polling);
		this.initializeAuthentication();
		this.setBankIdFlowSubscription();
	}

	onBankIdOnAnotherDeviceEvent() {
		this.resetValues();
		this.initializeAuthentication();
		this.errorComingFrom$.next("qr-code-error");
		this.setBankIdView(ViewOptions.qr);
	}

	onCreateAccountEvent() {
		this.closeDialog();
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.registration.register));
	}

	closeDialog() {
		this.matDialog.openDialogs?.find((dialog) => dialog.id === LOGIN_DIALOG_ID)?.close();
	}

	onCancelPollingEvent() {
		this.resetValues();
		this.cancelLastVerification();
		this.setBankIdView(ViewOptions.default);
	}

	onRetryBankIdEvent() {
		this.resetValues();
		this.errorComingFrom$.next("bank-id-start-error");
		this.setBankIdView(ViewOptions.polling);
		this.cancelLastVerification();
		this.initializeAuthentication();
		this.setBankIdFlowSubscription();
	}

	onQrCodeCurrentDeviceEvent() {
		this.resetValues();
		this.errorComingFrom$.next("bank-id-start-error");
		this.setBankIdView(ViewOptions.polling);
		this.cancelLastVerification();
		this.initializeAuthentication();
		this.setBankIdFlowSubscription();
	}

	onLoginWithEmailAndPasswordEvent() {
		this.store.dispatch(this.sgaLoginAction.selectLoginTab(SgaLoginTab.Generic));
	}

	resetValues() {
		this.invalidCredentialsError = false;
		this.initializeVerificationFail = false;
		this.genericLoginError = "";
	}

	onShowErrorAlertEvent(event: CustomEvent<SgaLoginError>) {
		this.alertService.open(
			{
				id: SGA_LOGIN_FLOW_BANKID_NOT_OPENED_ALERT_ID,
				title: event.detail?.title ?? `sga-login-transaction.notification.title`,
				...event.detail,
			},
			[TranslationGroup.Common]
		);
	}

	onOpenSupportPageInNewTabEvent() {
		this.customerSupportService.openSupportPageInNewTab();
		this.resetValues();
		this.setBankIdView(ViewOptions.default);
	}

	onCloseErrorAlertEvent() {
		this.resetValues();
		this.setBankIdView(ViewOptions.default);
	}

	setBankIdFlowSubscription() {
		if (this.openNewBankIdFlowAppSubscription$$) {
			this.openNewBankIdFlowAppSubscription$$.unsubscribe();
		}
		this.openNewBankIdFlowAppSubscription$$ = this.openNewBankIdFlowApp$?.subscribe();
	}

	fetchTranslations() {
		const accountBankIdArgs = LOGIN_BANKID_TRANSLATION_KEYS;

		this.translations$ = this.store.pipe(
			this.translationSelector.getByKeys_v2$(accountBankIdArgs.translations, [
				TranslationGroup.General,
				TranslationGroup.Common,
			]),
			map((keys) => Object.fromEntries(keys.map((_, i) => [accountBankIdArgs.translations[i], keys[i]])))
		);

		this.qrCodeComponentTranslations$ = this.store.pipe(
			this.translationSelector.getByKeys_v2$(accountBankIdArgs.qrCodeComponentTranslations, [
				TranslationGroup.Common,
			]),
			map((keys) =>
				Object.fromEntries(keys.map((_, i) => [accountBankIdArgs.qrCodeComponentTranslations[i], keys[i]]))
			)
		);

		this.images$ = combineLatest([
			this.store.select(this.imageSelector.getByKey(TranslationGroup.General, accountBankIdArgs.images[0])),
			this.store.select(this.imageSelector.getByKey(TranslationGroup.General, accountBankIdArgs.images[1])),
		]).pipe(map((images) => Object.fromEntries(images.map((_, i) => [accountBankIdArgs.images[i], images[i]]))));
	}

	resetAuthValues() {
		this.store.dispatch(this.authTransactionAction.reset());
		this.store.dispatch(this.authTransactionAction.resetVerificationStatus());
	}

	ngOnInit() {
		this.resetValues();
		this.resetAuthValues();
		this.fetchTranslations();

		this.qrCodeComponentView = QrComponentViewOptions.polling;
		if (this.isDesktop) {
			this.initializeAuthentication();
			this.errorComingFrom$.next("qr-code-error");
			this.setBankIdView(ViewOptions.qr);
		}

		this.openNewBankIdFlowApp$ = this.sgaLoginUiFlowService.openNewBankIdFlowApp$();

		this.pollingTimeout$ = this.sgaLoginUiFlowService.pollingTimeoutSubject$.asObservable();

		this.qrCode$ = this.store.select(this.authTransactionSelector.getQrCode()).pipe(
			ignoreNil(),
			tap(() => (this.qrCodeComponentView = QrComponentViewOptions.default)),
			switchMap((qrCode) =>
				interval(1000).pipe(
					take(qrCode.length),
					map((i) => qrCode[i])
				)
			)
		);

		const personalNumberErrors$ = this.sgaLoginUiFlowService.personalNumberErrors$().pipe(
			tap((error) => {
				this.genericLoginError = error;
			})
		);

		const loginTransactionsVerificationStatus$ = this.sgaLoginUiFlowService
			.loginTransactionsVerificationStatus$()
			.pipe(
				tap((status) => {
					if (
						status === VerificationStatus.Failed &&
						this.view === ViewOptions.qr &&
						!this.genericLoginError
					) {
						this.setBankIdView(ViewOptions.polling);
						this.genericLoginError = "bankid-not-responding-error";
					}
				})
			);
		const cancelLastVerification$ = this.sgaLoginUiFlowService.cancelLastVerification$();

		const customerVerificationCancel$ = this.sgaLoginUiFlowService.customerVerificationCancel$().pipe(
			tap(() => {
				this.genericLoginError = "error.text";
			})
		);

		const customerVerificationFail$ = this.sgaLoginUiFlowService.customerVerificationFail$().pipe(
			tap(() => {
				this.genericLoginError = "bankid-not-responding-error";
			})
		);

		const initializeVerificationFail$ = this.sgaLoginUiFlowService.initializeVerificationFail$().pipe(
			tap(() => {
				this.initializeVerificationFail = true;
			})
		);

		const invalidCredentialsError$ = this.sgaLoginUiFlowService.invalidCredentialsError$().pipe(
			tap(() => {
				this.setBankIdView(ViewOptions.polling);
				this.invalidCredentialsError = true;
			})
		);

		const genericLoginError$ = this.sgaLoginUiFlowService.genericLoginError$().pipe(
			tap((error) => {
				this.setBankIdView(ViewOptions.polling);
				this.genericLoginError = error ?? "error.text";
			})
		);

		this.data$$ = merge(
			personalNumberErrors$,
			loginTransactionsVerificationStatus$,
			cancelLastVerification$,
			initializeVerificationFail$,
			invalidCredentialsError$,
			customerVerificationFail$,
			customerVerificationCancel$,
			genericLoginError$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.cancelLastVerification();
		this.resetValues();
		this.resetAuthValues();

		this.data$$?.unsubscribe();
		this.openNewBankIdFlowAppSubscription$$?.unsubscribe();
	}
}
